import { createDefaults } from '../../utils/createDefaults';

type User = {
  nombre: string;
  apellido: string;
  dni: string;
  tel_celular: string;
  fecha_nacimiento: string | Date;
  genero: 'M' | 'F' | '';
};

type Address = {
  direccion: string;
  numero: string;
  piso?: string;
  departamento?: string;
  provincia: string;
  localidad: string;
};

export const User_Defaults = createDefaults<User>({
  nombre: '',
  apellido: '',
  dni: '',
  tel_celular: '',
  fecha_nacimiento: '',
  genero: '',
});

export const Address_Defaults = createDefaults<Address>({
  direccion: '',
  numero: '',
  piso: '',
  departamento: '',
  provincia: '',
  localidad: '',
});

type UserTransformed = {
  [K in keyof User as K extends 'fecha_nacimiento'
    ? 'fechaNac'
    : K extends 'tel_celular'
      ? 'telCelular'
      : K]?: User[K] | null;
};

type AddressTransformed = {
  [K in keyof Address as K extends 'departamento' ? 'dpto' : K]?: K extends 'localidad'
    ? number | null
    : Address[K] | null;
};

type LocalidadResponse =
  | number
  | string
  | {
      id?: number | string | null;
      value?: number | string | null;
      nombre?: string | null;
    };

type UserAddressRes = {
  direccion?: string | null;
  numero?: string | null;
  piso?: string | null;
  departamento?: string | null;
  provincia?: string | null;
  localidadId?: number | string | null;
  localidad_id?: number | string | null;
  localidad?: LocalidadResponse | null;
};

type UserPersonalRes = {
  nombre?: string | null;
  apellido?: string | null;
  dni?: string | null;
  fecha_nacimiento?: string | null;
  fechaNac?: string | null;
  fecha_nac?: string | null;
  email?: string | null;
  tel_celular?: string | null;
  telCelular?: string | null;
  genero?: 'M' | 'F' | '' | string | null;
};

// ======================
// REQUEST
// ======================
type UserReq = {
  datos_persona: User;
  datos_domicilio: AddressTransformed;
};

type SolicitudCambioUserReq = UserTransformed &
  AddressTransformed & {
    mascotaId: number;
  };

// ======================
// RESPONSE
// ======================
type UserRes = UserPersonalRes & UserAddressRes & {
  nombre: string;
  apellido: string;
  dni: string;
  fecha_nacimiento?: string;
  email: string;
  tel_celular: string;
  genero: 'M' | 'F' | '';
  datosDomicilio?: UserAddressRes | null;
  datos_domicilio?: UserAddressRes | null;
  domicilio?: UserAddressRes | null;
  datosPersona?: UserPersonalRes | null;
  datos_persona?: UserPersonalRes | null;
  persona?: UserPersonalRes | null;
};

const getPersonalData = (user: UserRes): UserPersonalRes =>
  user.datosPersona ?? user.datos_persona ?? user.persona ?? user;

const hasRootAddressData = (user: UserRes): boolean =>
  Boolean(
    user.direccion ||
      user.numero ||
      user.provincia ||
      user.localidadId ||
      user.localidad_id ||
      user.localidad,
  );

const getAddressData = (user: UserRes): UserAddressRes | null | undefined =>
  user.datosDomicilio ??
  user.datos_domicilio ??
  user.domicilio ??
  (hasRootAddressData(user) ? user : undefined);

const getLocalidadValue = (address?: UserAddressRes): number | string | null => {
  if (!address) return null;

  const localidad = address.localidad;

  if (address.localidadId !== undefined && address.localidadId !== null) {
    return address.localidadId;
  }

  if (address.localidad_id !== undefined && address.localidad_id !== null) {
    return address.localidad_id;
  }

  if (typeof localidad === 'object' && localidad !== null) {
    return localidad.id ?? localidad.value ?? localidad.nombre ?? null;
  }

  if (typeof localidad === 'number' || typeof localidad === 'string') {
    return localidad;
  }

  return null;
};

export const transformUser = (user?: UserRes) => {
  if (!user) {
    return { userData: null, addressData: null };
  }

  const personal = getPersonalData(user);
  const address = getAddressData(user);
  const fechaNacimiento =
    personal.fecha_nacimiento ?? personal.fechaNac ?? personal.fecha_nac ?? '';
  const telefono = personal.tel_celular ?? personal.telCelular ?? '';

  const userData: User = {
    nombre: personal.nombre || '',
    apellido: personal.apellido || '',
    dni: personal.dni || '',
    tel_celular: telefono || '',
    fecha_nacimiento: fechaNacimiento,
    genero: (personal.genero as 'M' | 'F' | '') || '',
  };

  const localidadValue = getLocalidadValue(address ?? undefined);

  const addressData: Address = {
    direccion: address?.direccion || '',
    numero: address?.numero || '',
    piso: address?.piso || '',
    departamento: address?.departamento || '',
    provincia: address?.provincia || '',
    localidad: localidadValue ? String(localidadValue) : '',
  };

  return { userData, addressData };
};

type LocalidadRes = {
  id: number;
  nombre: string;
  nombre_completo: string;
  provincia: string;
  codigo_postal: string;
  id_portal: number;
  createdAt: string;
  updatedAt: string;
};

export type {
  User,
  Address,
  UserReq,
  SolicitudCambioUserReq,
  UserRes,
  UserAddressRes,
  UserPersonalRes,
  LocalidadRes,
};
